import { Component, ElementRef, Input, Renderer2, ViewChild } from '@angular/core';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartConfiguration,
  ScriptableScaleContext,
} from 'chart.js/auto';
import { ChartRenderedService } from '../services/chart-rendered.service';
import { fitLabelFontSize } from './chart-label-font';

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const USUAL_LABEL_SIZE_PX = 18;
const LABEL_FONT_FAMILY = 'Open Sans';
// chart.js default y-axis ticks.padding
const TICK_PADDING_PX = 3;

@Component({
  selector: 'lib-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class LibChartComponent {

  @ViewChild('canvas') canvas: ElementRef | null = null; 

  @Input() max: number = 10;
  @Input() data: number[] = [];
  @Input() padding: number = 30;
  @Input() background = "#276CF7";
  @Input() labels: string[] = [];
  @Input() height: number = 0;
  @Input() base64 = false;

  chart: Chart | null = null;
  src: string | null = null

  private _label_sizes: number[] = [];
  private _label_space = 0;

  constructor(
    protected _chart_rendered_service: ChartRenderedService,
    private _renderer: Renderer2,
    private _host_ref: ElementRef<HTMLElement>
  )  {}

  ngAfterViewInit() {
    const canvas: HTMLCanvasElement = this.canvas?.nativeElement;
    this._fitLabelFonts(canvas.parentElement?.clientWidth ?? 0);

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: {
        labels: this.labels,
        datasets: [
          {
            data: this.data,
            backgroundColor: this.background,
            barThickness: 25,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, 
        indexAxis: 'y' as const,
        elements: {
          bar: {
            borderWidth: 0,
          },
        },
        layout: {
          padding: {
            left: this.padding
          }
        },
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            grid: {
              display: false,
            },
            ticks: {
              font: (ctx: ScriptableScaleContext) => ({
                size: this._label_sizes[ctx.index] ?? USUAL_LABEL_SIZE_PX,
                family: LABEL_FONT_FAMILY
              })
            },
          },
          x: {
            ticks: {
              font: {
                size: 16,
                family: 'Open Sans'
              }
            },
            max: this.max
          }
        },
        onResize: (chart, size) => {
          this._fitLabelFonts(size.width);
          chart.update('none');
        },
        animation: {
          onComplete: () => {
            this._chart_rendered_service.rendered();
          }
        }
      }
    }

    this.chart = new Chart(canvas, config);
  }

  ngOnChanges() {
    if(this.chart && this.base64) {
      this.src = this.chart.toBase64Image('high');
    }
  }

  // Mirrors chart.js 4.4.3: a vertical axis may take at most half the width left of the
  // layout padding. This is the only place that formula lives.
  // A non-positive width is a measurement of a detached or not-yet-laid-out canvas, not a
  // real size: the last valid fit (or the usual size, with a label space of 0 meaning
  // "no valid width seen yet") is kept, and the host attributes are still written.
  private _fitLabelFonts(chartWidth: number): void {
    if (chartWidth <= 0) {
      if (this._label_sizes.length !== this.labels.length) {
        this._label_sizes = this.labels.map(() => USUAL_LABEL_SIZE_PX);
      }
      this._writeLabelAttributes();
      return;
    }
    this._label_space = (chartWidth - this.padding - 0) / 2 - TICK_PADDING_PX;
    const context = this.canvas?.nativeElement.getContext('2d') as CanvasRenderingContext2D | null;
    const measure = (text: string, sizePx: number): number => {
      if (!context) {
        return 0;
      }
      context.font = `${sizePx}px ${LABEL_FONT_FAMILY}`;
      return context.measureText(text).width;
    };

    this._label_sizes = this.labels.map(label =>
      fitLabelFontSize(label, this._label_space, measure, USUAL_LABEL_SIZE_PX)
    );
    this._writeLabelAttributes();
  }

  private _writeLabelAttributes(): void {
    const host = this._host_ref.nativeElement;
    this._renderer.setAttribute(host, 'data-label-font-sizes', JSON.stringify(this._label_sizes));
    this._renderer.setAttribute(host, 'data-label-space', String(this._label_space));
  }
}
